// Ribbon trails for dash and projectiles.
// One merged triangle-strip draw call; configurable length; degrades with settings.

import * as THREE from 'three';
import type { Projectile, Player, Vec2, Enemy, Pickup } from '../types.js';
import { roleHueFor } from './instances.js';
import { cfg } from '../config.js';

const PLAYER_TRAIL_ID = -1;
const ENGINE_TRAIL_ID = -2;
/** Pickup trail ids live above every entity id (pools are ≤ a few thousand). */
const PICKUP_TRAIL_ID_OFFSET = 1_000_000;
/** Projectile trail ids get their own namespace too: entity pools recycle
 * ids across classes, so a fresh bullet can inherit a dead enemy's slot id —
 * without the offset it would adopt the enemy's stale chem-trail (wrong
 * width/color/params) instead of starting its gold comet tail. */
const PROJ_TRAIL_ID_OFFSET = 2_000_000;

const ARENA_SIZE = cfg.getNumber('sim.arena_size');
const HALF_ARENA = ARENA_SIZE / 2;

interface Trail {
  id: number;
  points: THREE.Vector3[];
  head: number; // index of newest point
  count: number;
  width: number;
  color: THREE.Color;
  dashTtl: number; // seconds; >0 for finite trails
  /** dashTtl's initial value — drives the dim-out while expiring (1 = full). */
  ttlTotal: number;
  maxSeg: number;
  /** Arc-length budget in world units; tail points past it are trimmed on push. */
  maxArc: number;
  /** Accumulated arc length of the live segments (world units). */
  arc: number;
  /** Per-slot world length of the segment ending at that point. */
  segLen: number[];
  /** Multiplies the ribbon's additive brightness (0.1 ≈ 10% transparency). */
  intensity: number;
  /** Head→tail brightness curve exponent (0.5 = sqrt, the shared default;
   * >1 concentrates brightness at the head — the comet read for bullets). */
  falloff: number;
  /** Brightness floor at the very tail (0 = tail fades to nothing). */
  tailFloor: number;
  /** Unwrap anchor: last raw sim position sampled (seam-safe deltas). */
  rawX: number;
  rawY: number;
  sampled: boolean;
  /** Last known velocity (u/s): an expiring ribbon's nose keeps traveling
   * along it — the rocket-exhaust fade, not a frozen line dimming out. */
  driftX: number;
  driftY: number;
  /** Fractional distance carried between drift samples. */
  driftAcc: number;
}

/** Trails sample by DISTANCE, not by frame: a new point lands every SAMPLE_DIST
 * world units of movement, so ribbon length is a property of the path, not
 * of speed × framerate (the chem-trail look — long ribbons behind everything). */
const SAMPLE_DIST = 0.15;
/** A single-tick raw wrapped jump past this (world units) breaks the ribbon:
 * teleports and recycled ids otherwise streak a line from the old spot. */
const JUMP_BREAK = 8;
/** A dead/collected entity's ribbon keeps drawing this long while dimming. */
const DEATH_TRAIL_TTL = 0.8;

export class TrailSystem {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly geometry: THREE.BufferGeometry;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly trails = new Map<number, Trail>();
  private readonly maxSegments: number;
  private readonly baseMaxSegPerTrail: number;
  private maxSegPerTrail: number;
  private vertexCount = 0;
  private readonly tmpColor = new THREE.Color();

  constructor(maxSegments = 4096, maxSegPerTrail = 28) {
    this.maxSegments = maxSegments;
    this.baseMaxSegPerTrail = maxSegPerTrail;
    this.maxSegPerTrail = maxSegPerTrail;
    const maxVerts = maxSegments * 6;
    this.positions = new Float32Array(maxVerts * 3);
    this.colors = new Float32Array(maxVerts * 3);
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));
    this.geometry.setDrawRange(0, 0);
    this.material = new THREE.MeshBasicMaterial({
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
  }

  setLengthScale(scale: number): void {
    // scale 0..1; affects effective max segments per trail.
    const target = Math.max(2, Math.floor(this.baseMaxSegPerTrail * scale));
    this.maxSegPerTrail = target;
  }

  /** Camera anchor in unwrapped world space; set once per frame by the renderer. */
  private readonly camAnchor: Vec2 = { x: 0, y: 0 };

  setCameraAnchor(anchor: Vec2): void {
    this.camAnchor.x = anchor.x;
    this.camAnchor.y = anchor.y;
  }

  // Transform a raw sim coordinate into the camera's unwrapped frame: identity
  // away from the torus seam, re-anchored by ±ARENA near it so trail vertices
  // always sit where the entity's sprite renders. Branch-based, no modulo.
  private toCameraX(x: number): number {
    let d = x - this.camAnchor.x;
    if (d > HALF_ARENA) d -= ARENA_SIZE; else if (d < -HALF_ARENA) d += ARENA_SIZE;
    return this.camAnchor.x + d;
  }

  private toCameraY(y: number): number {
    let d = y - this.camAnchor.y;
    if (d > HALF_ARENA) d -= ARENA_SIZE; else if (d < -HALF_ARENA) d += ARENA_SIZE;
    return this.camAnchor.y + d;
  }

  startTrail(id: number, width: number, color: number, finite = false, maxSeg?: number, intensity = 1, maxArc = Infinity): void {
    if (this.trails.has(id)) return;
    const seg = Math.max(2, Math.min(maxSeg ?? this.maxSegPerTrail, this.maxSegPerTrail));
    this.trails.set(id, {
      id,
      points: Array.from({ length: seg + 1 }, () => new THREE.Vector3()),
      head: 0,
      count: 0,
      width,
      color: new THREE.Color(color),
      dashTtl: finite ? 0.3 : -1,
      ttlTotal: finite ? 0.3 : 0,
      maxSeg: seg,
      maxArc,
      arc: 0,
      segLen: new Array(seg + 1).fill(0),
      intensity,
      falloff: 0.5,
      tailFloor: 0.1,
      rawX: 0,
      rawY: 0,
      sampled: false,
      driftX: 0,
      driftY: 0,
      driftAcc: 0,
    });
  }

  removeTrail(id: number): void {
    this.trails.delete(id);
  }

  /** Drop every ribbon. Run restarts rewind the shared entity id source to 1,
   * so without this a stale ribbon (e.g. an attract-mode enemy's) is adopted
   * by the new run's entity with the same id — wrong width/color/params. */
  reset(): void {
    this.trails.clear();
  }

  updatePoint(id: number, pos: Vec2): void {
    const trail = this.trails.get(id);
    if (!trail) return;
    let segLen = 0;
    if (trail.sampled) {
      // Distance gate: skip until the entity has moved SAMPLE_DIST from the
      // last sampled point (toroidal shortest delta for seam safety).
      const dx = pos.x - trail.rawX;
      const dy = pos.y - trail.rawY;
      const wdx = dx > HALF_ARENA ? dx - ARENA_SIZE : dx < -HALF_ARENA ? dx + ARENA_SIZE : dx;
      const wdy = dy > HALF_ARENA ? dy - ARENA_SIZE : dy < -HALF_ARENA ? dy + ARENA_SIZE : dy;
      const d2 = wdx * wdx + wdy * wdy;
      if (d2 < SAMPLE_DIST * SAMPLE_DIST) return;
      if (d2 > JUMP_BREAK * JUMP_BREAK) {
        // Teleport / recycled id: re-seed the ring buffer like the first
        // sample so no segment spans the jump (kills seam/teleport streaks
        // regardless of the camera-frame mapping).
        trail.count = 0;
      } else {
        segLen = Math.sqrt(d2);
      }
    }
    trail.rawX = pos.x;
    trail.rawY = pos.y;
    trail.sampled = true;
    this.pushPoint(trail, pos, segLen);
  }

  /** Append one camera-frame point to the ribbon's ring buffer. */
  private pushPoint(trail: Trail, pos: Vec2, segLen = SAMPLE_DIST): void {
    const x = this.toCameraX(pos.x);
    const y = this.toCameraY(pos.y);
    const total = trail.points.length;
    trail.head = (trail.head + 1) % total;
    trail.points[trail.head].set(x, y, 0.06);
    if (trail.count === 0) {
      // First real sample: seed every ring-buffer slot with it so the ribbon
      // never draws a segment from the stale (0,0,0) construction value to
      // the entity — that read as a long line from the world origin.
      for (const pt of trail.points) pt.set(x, y, 0.06);
      trail.segLen.fill(0);
      trail.arc = 0;
      segLen = 0;
    } else if (trail.count === total - 1) {
      // Buffer full: this push evicts the tail point, so drop the evicted
      // segment (stored just past the new head) from the arc tally.
      trail.arc -= trail.segLen[(trail.head + 1) % total];
    }
    trail.segLen[trail.head] = segLen;
    trail.arc += segLen;
    if (trail.count < total - 1) trail.count++;
    // Arc-length cap in world units: trim tail points while over budget, so
    // a fast mover's ribbon stays short no matter its speed.
    while (trail.count > 1 && trail.arc > trail.maxArc) {
      trail.arc -= trail.segLen[(trail.head - trail.count + 1 + total) % total];
      trail.count--;
    }
  }

  startDash(pos: Vec2, _dir: Vec2, width: number, color: number): void {
    this.removeTrail(PLAYER_TRAIL_ID);
    this.startTrail(PLAYER_TRAIL_ID, width, color, true);
    this.updatePoint(PLAYER_TRAIL_ID, pos);
  }

  endDash(): void {
    // Let the finite trail decay naturally.
  }

  updateFromWorld(
    player: Player,
    projectiles: Iterable<Projectile>,
    enemies: Iterable<Enemy>,
    pickups: Iterable<Pickup>,
    _time: number
  ): void {
    const activeIds = new Set<number>();

    // Maintain projectile trails: a short comet tail — brightness concentrated
    // at the head, fading to nothing (no floor) so the pair sprite+ribbon
    // reads as a moving dart in a still, not a uniform line. Velocity is
    // cached on the trail so a dying shot's ribbon keeps flying forward while
    // it fades (rocket exhaust).
    for (const p of projectiles) {
      const id = PROJ_TRAIL_ID_OFFSET + p.id;
      activeIds.add(id);
      const trail = this.trails.get(id);
      if (!trail) {
        this.startTrail(id, p.hitbox_radius, 0xffc45e, false, 24, 0.6, 3.2);
      }
      const t = this.trails.get(id)!;
      t.falloff = 1.8;
      t.tailFloor = 0;
      t.driftX = p.vel.x;
      t.driftY = p.vel.y;
      this.updatePoint(id, p.pos);
    }

    // Enemy motion streaks in the enemy's ROLE hue (30% transparency):
    // thick enough to read individually, short enough to fade behind.
    // Fast archetypes get a little more reach.
    for (const e of enemies) {
      activeIds.add(e.id);
      if (!this.trails.has(e.id)) {
        const fast = e.def_id === 'enemy.dartling' || e.def_id === 'enemy.leech' || e.def_id === 'enemy.weaver';
        this.startTrail(e.id, 0.135, roleHueFor(e.def_id) ?? 0xd8384e, false, fast ? 55 : 40, 0.3, 6);
      }
      this.updatePoint(e.id, e.pos);
    }

    // Coin trails: a short green glimmer behind motes while the magnet pulls
    // them in (stationary drops leave none). Ids are offset out of the
    // enemy/projectile id space.
    for (const pk of pickups) {
      const id = PICKUP_TRAIL_ID_OFFSET + pk.id;
      const speed = Math.hypot(pk.vel.x, pk.vel.y);
      if (speed > 0.5) {
        activeIds.add(id);
        if (!this.trails.has(id)) {
          this.startTrail(id, 0.105, 0x7dff4f, false, 16, 0.22);
        }
        this.updatePoint(id, pk.pos);
      } else {
        this.removeTrail(id);
      }
    }

    // Stale trails (dead enemies, expired projectiles, collected pickups):
    // keep drawing for DEATH_TRAIL_TTL while dimming to nothing — the kill
    // leaves a fading comet tail instead of a snapped-off line.
    for (const id of Array.from(this.trails.keys())) {
      if (id >= 0 && !activeIds.has(id)) {
        const trail = this.trails.get(id)!;
        if (trail.dashTtl < 0) {
          trail.dashTtl = DEATH_TRAIL_TTL;
          trail.ttlTotal = DEATH_TRAIL_TTL;
        }
      }
    }

    // Engine wake behind the ship while moving: a short cyan velocity ribbon
    // (~7 u), hue-separated from the gold dash trail and kill flashes so it
    // reads as thrust, not as a combat event. On stop it LINGERS for a beat
    // (1.2 s TTL) instead of vanishing.
    const speed = Math.hypot(player.vel.x, player.vel.y);
    if (speed > 0.5) {
      const trail = this.trails.get(ENGINE_TRAIL_ID);
      if (!trail) {
        this.startTrail(ENGINE_TRAIL_ID, 0.22, 0x35e0ff, false, 80, 0.3, 7);
      } else {
        trail.dashTtl = -1; // moving again: cancel the linger countdown
      }
      this.updatePoint(ENGINE_TRAIL_ID, player.pos);
    } else {
      const trail = this.trails.get(ENGINE_TRAIL_ID);
      if (trail && trail.dashTtl < 0) {
        trail.dashTtl = 1.2;
        trail.ttlTotal = 1.2;
      }
    }

    // Update player dash trail (bright gold, wider than the hull).
    if (player.dash.active) {
      if (!this.trails.has(PLAYER_TRAIL_ID)) {
        this.startTrail(PLAYER_TRAIL_ID, player.hitbox_radius * 0.9, 0xffd88a, true, undefined, 0.65);
      }
      this.updatePoint(PLAYER_TRAIL_ID, player.pos);
    }
  }

  update(dt: number): void {
    for (const trail of this.trails.values()) {
      if (trail.dashTtl > 0) {
        // Rocket exhaust: while expiring with a recorded velocity, the
        // ribbon's nose keeps traveling (up to the ring buffer's length).
        const speed = Math.hypot(trail.driftX, trail.driftY);
        if (speed > 1e-3 && trail.sampled) {
          trail.driftAcc += speed * dt;
          while (trail.driftAcc >= SAMPLE_DIST) {
            trail.driftAcc -= SAMPLE_DIST;
            trail.rawX += (trail.driftX / speed) * SAMPLE_DIST;
            trail.rawY += (trail.driftY / speed) * SAMPLE_DIST;
            this.pushPoint(trail, { x: trail.rawX, y: trail.rawY });
          }
        }
        trail.dashTtl -= dt;
        if (trail.dashTtl <= 0) {
          this.removeTrail(trail.id);
        }
      }
    }
    this.rebuild();
  }

  private rebuild(): void {
    let v = 0;
    for (const trail of this.trails.values()) {
      if (trail.count < 2) continue;
      // Expiring trails dim to nothing over their TTL (death fade-out).
      const dim = trail.dashTtl > 0 && trail.ttlTotal > 0 ? Math.max(0, trail.dashTtl / trail.ttlTotal) : 1;
      const total = trail.points.length;
      const tailIdx = (trail.head - trail.count + total) % total;
      let prev = trail.points[tailIdx];
      for (let i = 1; i <= trail.count; i++) {
        const idx = (tailIdx + i) % total;
        const cur = trail.points[idx];
        if (v + 6 > this.maxSegments * 6) break;
        const t0 = (i - 1) / trail.count;
        const t1 = i / trail.count;
        const w0 = trail.width * (1 - t0);
        const w1 = trail.width * (1 - t1);
        const dx = cur.x - prev.x;
        const dy = cur.y - prev.y;
        // Seam guard: when the entity sits near the arena's antipode to the
        // camera anchor, toCameraX flips sides between samples and consecutive
        // points land ~an arena-width apart — drawing that segment streaks a
        // line across the whole screen. Break the ribbon there instead.
        if (Math.abs(dx) > HALF_ARENA || Math.abs(dy) > HALF_ARENA) {
          prev = cur;
          continue;
        }
        const len = Math.hypot(dx, dy) || 1;
        const nx = -dy / len;
        const ny = dx / len;
        // Fade along the ribbon: per-trail falloff curve (sqrt by default so
        // a long trail's body stays visible end to end — the chem-trail look;
        // steeper for bullets so the tail dies and the head carries the read).
        // `dim` fades the whole ribbon while it expires.
        const f0 = (trail.tailFloor + (1 - trail.tailFloor) * Math.pow(t0, trail.falloff)) * trail.intensity * dim;
        const f1 = (trail.tailFloor + (1 - trail.tailFloor) * Math.pow(t1, trail.falloff)) * trail.intensity * dim;
        // four vertices for ribbon segment
        this.tmpColor.copy(trail.color).multiplyScalar(f0);
        this.setVert(v++, prev.x + nx * w0, prev.y + ny * w0, this.tmpColor);
        this.tmpColor.copy(trail.color).multiplyScalar(f1);
        this.setVert(v++, cur.x + nx * w1, cur.y + ny * w1, this.tmpColor);
        this.tmpColor.copy(trail.color).multiplyScalar(f0);
        this.setVert(v++, prev.x - nx * w0, prev.y - ny * w0, this.tmpColor);
        this.setVert(v++, prev.x - nx * w0, prev.y - ny * w0, this.tmpColor);
        this.tmpColor.copy(trail.color).multiplyScalar(f1);
        this.setVert(v++, cur.x + nx * w1, cur.y + ny * w1, this.tmpColor);
        this.setVert(v++, cur.x - nx * w1, cur.y - ny * w1, this.tmpColor);
        prev = cur;
      }
    }
    this.vertexCount = v;
    this.geometry.setDrawRange(0, this.vertexCount);
    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.color.needsUpdate = true;
  }

  private setVert(i: number, x: number, y: number, color: THREE.Color): void {
    const j = i * 3;
    this.positions[j] = x;
    this.positions[j + 1] = y;
    this.positions[j + 2] = 0.05;
    this.colors[j] = color.r;
    this.colors[j + 1] = color.g;
    this.colors[j + 2] = color.b;
  }
}
